import React, { useContext, useState, useEffect } from 'react';
import { Collapse } from 'reactstrap';
import Checkbox from '@minecraft.minusable-checkbox';
import styled from 'styled-components';
import { deeplyTraverseChildren, includesAll, haveSameContents } from '@minecraft.utils';
import _filter from 'lodash/filter';
import _uniq from 'lodash/uniq';
import _uniqBy from 'lodash/uniqBy';
import _findIndex from 'lodash/findIndex';
import UncontrolledArrow from '@minecraft.uncontrolled-arrow';
import SelectableDropdownContext from '../SelectableSearchDropdownContext';

const NestedCollapse = styled(Collapse)`
  padding-left: 1.25rem;
`;

const SectionHeader = styled.div`
  padding-bottom: 0.25rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StyledArrow = styled(UncontrolledArrow)<{ onClick: () => void }>`
  margin: 0 0.5rem 0.75rem 0.5rem;
  height: 1rem;
`;

export type Props = {
  children?: React.ReactNode;
  name: string;
  isToggleable?: boolean;
};

export default ({ children, name, isToggleable = true, isOpen = false, isBold = false, hideCheckbox = false }) => {
  const [isOpened, setIsOpened] = useState(isOpen);
  const [sectionOptions, setSectionOptions] = useState([]);
  const { saveAsOption, options, setOptions, filter, visibilityPredicate } = useContext(SelectableDropdownContext);
  const [isVisible, setIsVisible] = useState(true);

  const includeAtLeastOne = (holeArr, values): boolean => {
    return values.some(val =>
      saveAsOption ? _findIndex(holeArr, item => item.value === val.value) !== -1 : holeArr.includes(val)
    );
  };

  // Initialize options for which given section will be responsible (deeply):
  useEffect(() => {
    deeplyTraverseChildren(children, child => {
      if (child.props.value !== undefined) {
        setSectionOptions(actualSectionOptions => [
          ...actualSectionOptions,
          saveAsOption ? { value: child.props.value, label: child.props.label } : child.props.value,
        ]);
      }
    });
  }, []);

  useEffect(() => {
    // if at least one of child checkboxes is selected - section is open
    if (includeAtLeastOne(options, sectionOptions)) setIsOpened(true);
  }, [sectionOptions]);

  // detect whether the section should be displayed but the filter or not
  useEffect(() => {
    let shouldBeVisible = false;
    // if at least one child is visible by the filter, the section should be visible too
    deeplyTraverseChildren(children, child => {
      if (!shouldBeVisible && child.props.label && visibilityPredicate(child.props.label, filter)) {
        shouldBeVisible = true;
      }
    });
    setIsVisible(shouldBeVisible);
    // if filter exists and section is visible, open it
    if (filter.length > 0 && !isOpened && shouldBeVisible) {
      setIsOpened(true);
    }
  }, [filter]);

  const isChecked = saveAsOption ? haveSameContents(options, sectionOptions) : includesAll(options, sectionOptions);

  const showMinusInCheckBox = !isChecked && includeAtLeastOne(options, sectionOptions);

  return (
    isVisible && (
      <>
        <SectionHeader>
          {hideCheckbox ? (
            <div> {name} </div>
          ) : (
            <Checkbox
              isBold={isBold}
              onChange={() => {
                if (isChecked) {
                  setOptions(
                    _filter(options, option =>
                      saveAsOption
                        ? _findIndex(sectionOptions, sectionOption => sectionOption.value === option.value) === -1
                        : !sectionOptions.includes(option)
                    )
                  );
                } else {
                  setOptions(
                    saveAsOption
                      ? _uniqBy([...options, ...sectionOptions], 'value')
                      : _uniq([...options, ...sectionOptions])
                  );
                }
              }}
              isChecked={isChecked}
              label={name}
              minusEnable={showMinusInCheckBox}
              data-qa-id={`selectable-dropdown-section-${name}`}
            />
          )}
          {isToggleable && <StyledArrow onClick={() => setIsOpened(!isOpened)} />}
        </SectionHeader>
        <NestedCollapse isOpen={isOpened || !isToggleable}>{children}</NestedCollapse>
      </>
    )
  );
};
